import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchClientsAsync,
  createClientAsync,
  updateClientAsync,
  deleteClientAsync,
  searchClients,
  clearError
} from '../../redux/features/clientSlice';
import { getUser, getCompany } from '../../redux/features/authSlice';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getCurrentPackage, getMaxClients, canAddClient } from '../../utils/packageLimits';
import { 
  Users, UserPlus, Edit, Trash2, Mail, MapPin, Clock, Star,
  Calendar, Search, PlusCircle, X, Pencil, Phone, FileText, UserX, Loader2, Globe, List,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import './ClientsView.css';
import LoadingSpinner from '../ui/LoadingSpinner';
import CustomPhoneInput from '../shared/PhoneInput';
import { getJobs, fetchArchiveJobs } from '../../redux/features/jobSlice';
import { getWorkers } from '../../redux/features/workerSlice';
import { getPartners } from '../../redux/features/partnerSlice';
import { getWorkerName, getWorkerColor, getWorkerInitials } from '../../utils/workerUtils';
import { formatDisplayValue, getJobDisplayStatus } from '../../utils/displayUtils';
import JobForm from '../forms/JobForm';

// Phone prefixes mapping
const COUNTRY_PREFIXES = {
  rs: '381', // Serbia
  ba: '387', // Bosnia and Herzegovina
  hr: '385', // Croatia
  me: '382', // Montenegro
  mk: '389', // North Macedonia
  si: '386'  // Slovenia
};

// Country names mapping
const COUNTRY_NAMES = {
  ba: 'Bosnia and Herzegovina',
  rs: 'Serbia',
  hr: 'Croatia',
  me: 'Montenegro',
  mk: 'North Macedonia',
  si: 'Slovenia'
};

const ClientsView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { clients, filteredClients, loading, error } = useSelector((state) => state.client);
  const { workers } = useSelector((state) => state.worker || { workers: [] });
  const { partners } = useSelector((state) => state.partner || { partners: [] });
  const { user } = useSelector((state) => state.auth);
  const [showForm, setShowForm] = useState(false);
  const [currentClient, setCurrentClientState] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedRating, setSelectedRating] = useState('all');
  const [formError, setFormError] = useState('');
  const [historyClient, setHistoryClient] = useState(null);
  const lastHistoryJobsFetchKeyRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const clientsPerPage = 10;
  const subscriptionPackage = user?.result?.subscriptionPackage || 'free';
  const packageUsage = user?.result?.packageUsage || {};
  const jobState = useSelector((state) => state.job || { jobs: [], archiveJobs: [], loading: false, archiveLoading: false });
  const jobList = Array.isArray(jobState.jobs) ? jobState.jobs : [];
  const archiveJobList = Array.isArray(jobState.archiveJobs) ? jobState.archiveJobs : [];
  const jobsLoading = jobState.loading;
  const archiveLoading = jobState.archiveLoading;
  const [createJobPrefill, setCreateJobPrefill] = useState(null);
  
  // Get user country code safely
  const userCountryCode = user?.result?.countryCode?.toLowerCase() || 'rs'; // Default to Serbia if not set
  const phonePrefix = COUNTRY_PREFIXES[userCountryCode] || '381'; // Default to Serbian prefix if country code not found
  const defaultPhonePrefix = `+${phonePrefix}`;
  
  // Initialize form data - use safe defaults
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: defaultPhonePrefix, // Initialize with prefix
    countryCode: userCountryCode,  // Will be set from user's country code
    street: '',
    number: '',
    floor: '',
    apartment: '',
    isHouse: false,
    description: '',
    rating: ''
  });
  
  // Initialize form data with user's country code and phone prefix when user is available
  useEffect(() => {
    if (user?.result?.countryCode) {
      const countryCode = user.result.countryCode.toLowerCase();
      const prefix = COUNTRY_PREFIXES[countryCode] || '381';
      const phonePrefixValue = `+${prefix}`;
      
      setFormData(prev => ({
        ...prev,
        countryCode: countryCode,
        // Initialize phone with prefix if it's empty or doesn't have prefix
        phone: prev.phone && prev.phone.startsWith('+') ? prev.phone : phonePrefixValue
      }));
    }
  }, [user?.result?.countryCode]);

  // Load clients, jobs, and workers on component mount
  useEffect(() => {
    dispatch(fetchClientsAsync());
    dispatch(getJobs());
    dispatch(getWorkers());
    dispatch(getPartners());
  }, [dispatch]);

  // Refresh user profile to get updated packageUsage when component mounts or clients change
  useEffect(() => {
    const refreshPackageUsage = async () => {
      try {
        console.log('🔄 Refreshing package usage...');
        const result = await dispatch(getCompany());
        console.log('✅ Package usage refreshed:', result);
        if (result?.payload?.packageUsage) {
          console.log('📊 Updated addressesThisMonth:', result.payload.packageUsage.addressesThisMonth);
        }
      } catch (err) {
        console.error('❌ Failed to refresh company profile:', err);
      }
    };
    
    refreshPackageUsage();
  }, [dispatch, clients.length]); // Refresh when clients list changes
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Phone input is now handled by CustomPhoneInput component
    if (name === 'phone') {
      return;
    }

    if (name === 'searchTerm') {
      setSearchTerm(value);
      dispatch(searchClients(value));
      setCurrentPage(1); // Reset to first page when searching
      return;
    }

    if (name === 'isHouse') {
      const boolValue = typeof value === 'boolean' ? value : value === 'true';
      setFormData(prev => ({
        ...prev,
        isHouse: boolValue
      }));
      return;
    }

    if (name === 'rating') {
      setFormData(prev => ({
        ...prev,
        rating: value
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const normalizeString = (val) => {
      if (val === undefined || val === null) return undefined;
      const trimmed = typeof val === 'string' ? val.trim() : val;
      if (trimmed === '') return null;
      return trimmed;
    };

    const trimmedEmail = normalizeString(formData.email);
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setFormError('Unesite validnu email adresu');
      return;
    }

    const ratingValue = formData.rating ? Number(formData.rating) : null;
    if (formData.rating && (!Number.isFinite(ratingValue) || ratingValue < 1 || ratingValue > 10)) {
      setFormError('Ocena mora biti broj između 1 i 10');
      return;
    }

    const address = {};
    let includeAddress = false;
    const streetValue = normalizeString(formData.street);
    if (streetValue !== undefined) {
      address.street = streetValue;
      includeAddress = true;
    }

    const numberValue = normalizeString(formData.number);
    if (numberValue !== undefined) {
      address.number = numberValue;
      includeAddress = true;
    }

    if (!formData.isHouse) {
      const floorValue = normalizeString(formData.floor);
      if (floorValue !== undefined) {
        address.floor = floorValue;
        includeAddress = true;
      }

      const apartmentValue = normalizeString(formData.apartment);
      if (apartmentValue !== undefined) {
        address.apartment = apartmentValue;
        includeAddress = true;
      }
    } else {
      address.floor = null;
      address.apartment = null;
      includeAddress = true;
    }

    const countryValue = normalizeString(formData.countryCode || userCountryCode);
    if (countryValue !== undefined && countryValue !== null) {
      address.countryCode = countryValue;
      includeAddress = true;
    }

    if (formData.isHouse || includeAddress) {
      address.isHouse = Boolean(formData.isHouse);
      includeAddress = true;
    }

    const clientData = {};
    const nameValue = normalizeString(formData.name);
    if (nameValue !== undefined) clientData.name = nameValue;

    const phoneValue = normalizeString(formData.phone);
    if (phoneValue !== undefined) clientData.phone = phoneValue;

    if (trimmedEmail !== undefined) clientData.email = trimmedEmail;

    if (includeAddress) {
      clientData.address = address;
    }

    const descriptionValue = normalizeString(formData.description);
    if (descriptionValue !== undefined) clientData.description = descriptionValue;

    if (ratingValue !== null) {
      clientData.rating = ratingValue;
    } else if (currentClient && formData.rating === '') {
      clientData.rating = null;
    }

    try {
      if (showForm && currentClient) {
        if (!currentClient._id) {
          toast.error('ID klijenta nedostaje. Molimo osvežite stranicu i pokušajte ponovo.');
          return;
        }
        // Dispatch update and check Redux state instead of using unwrap()
        const updateResult = await dispatch(updateClientAsync({ 
          id: currentClient._id, 
          updatedClientData: clientData 
        }));
        
        // Check if the action was fulfilled (successful) by checking the result
        if (updateClientAsync.fulfilled.match(updateResult)) {
          // Update was successful - Redux state is already updated
          toast.success('Klijent je uspešno ažuriran');
          resetForm();
          // Refresh clients list to ensure UI is in sync
          await dispatch(fetchClientsAsync());
        } else if (updateClientAsync.rejected.match(updateResult)) {
          // Update was rejected - check if it's a CompanyModel error
          const errorPayload = updateResult.payload || {};
          const errorMessage = errorPayload.message || errorPayload.data?.error || '';
          const isCompanyModelError = errorMessage.includes("Cannot read properties of undefined (reading 'findById')") ||
                                     errorMessage.includes('findById');
          
          if (isCompanyModelError) {
            // Check Redux state - if client was updated, ignore the error
            const updatedClientInState = clients.find(c => c._id === currentClient._id);
            const hasChanges = updatedClientInState && (
              updatedClientInState.name !== currentClient.name ||
              updatedClientInState.email !== currentClient.email ||
              updatedClientInState.phone !== currentClient.phone
            );
            
            if (hasChanges || updatedClientInState) {
              // Client was updated in Redux state, ignore the error
              toast.success('Klijent je uspešno ažuriran');
              resetForm();
              await dispatch(fetchClientsAsync());
              return; // Exit early, don't show error
            }
          }
          
          // For other errors or if client wasn't updated, show error
          const wrappedError = {
            payload: errorPayload,
            message: errorPayload.message || 'An error occurred while updating the client'
          };
          throw wrappedError; // Re-throw to be caught by outer catch
        }
      } else {
        try {
          const result = await dispatch(createClientAsync(clientData)).unwrap();
          toast.success('Klijent je uspešno dodat');
          resetForm();
          await dispatch(fetchClientsAsync());
          
          // Refresh user profile to get updated packageUsage
          try {
            // Try getCompany first (more reliable), fallback to getUser
            try {
              await dispatch(getCompany());
            } catch (companyError) {
              await dispatch(getUser());
            }
          } catch (refreshError) {
            console.warn('Failed to refresh user profile after client creation:', refreshError);
            // Don't fail the whole operation if profile refresh fails
          }
        } catch (createError) {
          throw createError; // Re-throw to be caught by outer catch
        }
      }
    } catch (error) {
      
      // Always close the form/modal even on error
      // But only if it's not a validation error that user should fix
      const isValidationError = (error?.payload?.message && error.payload.message.includes('Validation')) || 
                                (error?.response?.data?.message && error.response.data.message.includes('Validation')) ||
                                (typeof error?.payload === 'string' && error.payload.includes('Validation'));
      
      // Handle limit reached error
      if (error?.limitReached || error?.payload?.limitReached) {
        const limitData = error?.payload || error;
        const limitMessage = limitData.message || `Dostigli ste mesečni limit od ${limitData.limit || 100} adresa za Free paket.`;
        setFormError(limitMessage);
        toast.error(limitMessage, {
          duration: 6000,
        });
        if (!isValidationError) {
          resetForm(); // Close modal on limit error
        }
      } else {
        // Extract error message from various possible locations
        let errorMessage = 'Greška pri čuvanju klijenta';
        
        // Check if error is a string
        if (typeof error === 'string') {
          errorMessage = error;
        }
        // Check error.payload (from Redux thunk rejectWithValue)
        else if (error?.payload) {
          if (typeof error.payload === 'string') {
            errorMessage = error.payload;
          } else if (error.payload?.data?.error) {
            // Prioritize the actual error message from server
            errorMessage = error.payload.data.error;
          } else if (error.payload?.data?.message) {
            errorMessage = error.payload.data.message;
          } else if (error.payload?.error) {
            errorMessage = error.payload.error;
          } else if (error.payload?.message) {
            errorMessage = error.payload.message;
          } else if (error.payload?.data) {
            // If data is a string or object
            if (typeof error.payload.data === 'string') {
              errorMessage = error.payload.data;
            } else if (error.payload.data?.message) {
              errorMessage = error.payload.data.message;
            } else {
              errorMessage = JSON.stringify(error.payload.data);
            }
          } else {
            // If payload is an object but no message found, stringify it
            errorMessage = JSON.stringify(error.payload);
          }
        }
        // Check error.response (from axios)
        else if (error?.response?.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data?.error) {
            errorMessage = error.response.data.error;
          }
        }
        // Check error.message
        else if (error?.message) {
          errorMessage = error.message;
        }
        
        setFormError(errorMessage);
        toast.error(errorMessage);
        
        // Close modal on non-validation errors
        if (!isValidationError) {
          resetForm();
        }
      }
    }
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: defaultPhonePrefix, // Initialize with prefix
      countryCode: userCountryCode, // Use the current user's country code
      street: '',
      number: '',
      floor: '',
      apartment: '',
      isHouse: false,
      description: '',
      rating: ''
    });
    setCurrentClientState(null);
    setShowForm(false);
  };
  
  // Helper function to parse address string into object
  const parseAddressString = (addressString) => {
    if (!addressString || typeof addressString !== 'string') {
      return { street: '', number: '', floor: '', apartment: '' };
    }
    
    const trimmed = addressString.trim();
    if (!trimmed) {
      return { street: '', number: '', floor: '', apartment: '' };
    }
    
    // Split by spaces
    const parts = trimmed.split(/\s+/).filter(p => p.length > 0);
    
    if (parts.length === 0) {
      return { street: '', number: '', floor: '', apartment: '' };
    }
    
    // Helper function to check if a string is a number (can contain digits, /, -, etc.)
    const isNumber = (str) => {
      if (!str) return false;
      // Check if string starts with a digit and is mostly numeric
      // Allow formats like "17", "17/5", "17A", etc.
      return /^\d+[\d\/\-A-Za-z]*$/.test(str);
    };
    
    // Find numbers from the end
    // Strategy: Look for numbers from the end, and assume:
    // - Last number is apartment (if there are multiple numbers)
    // - Second to last number is floor (if there are 2+ numbers)
    // - Third to last number is address number (if there are 3+ numbers)
    // - Everything before the address number is street
    
    const numberIndices = [];
    for (let i = parts.length - 1; i >= 0; i--) {
      if (isNumber(parts[i])) {
        numberIndices.unshift(i); // Add to beginning to keep order
      }
    }
    
    if (numberIndices.length === 0) {
      // No numbers found, everything is street
      return { street: parts.join(' '), number: '', floor: '', apartment: '' };
    }
    
    // Determine which number is which based on position
    let streetEndIndex;
    let number = '';
    let floor = '';
    let apartment = '';
    
    if (numberIndices.length === 1) {
      // Only one number found - it's the address number
      streetEndIndex = numberIndices[0];
      number = parts[numberIndices[0]];
    } else if (numberIndices.length === 2) {
      // Two numbers found - last is apartment, first is address number
      streetEndIndex = numberIndices[0];
      number = parts[numberIndices[0]];
      apartment = parts[numberIndices[1]];
    } else if (numberIndices.length >= 3) {
      // Three or more numbers found - last is apartment, second to last is floor, third to last is address number
      streetEndIndex = numberIndices[0];
      number = parts[numberIndices[0]];
      floor = parts[numberIndices[1]];
      apartment = parts[numberIndices[2]];
    }
    
    const street = parts.slice(0, streetEndIndex).join(' ');
    
    return {
      street: street,
      number: number,
      floor: floor,
      apartment: apartment
    };
  };

  const handleEdit = (client) => {
    setCurrentClientState(client);
    
    console.log('📝 handleEdit - Client address data:', {
      clientAddress: client.address,
      addressType: typeof client.address,
      street: client.address?.street,
      number: client.address?.number,
      floor: client.address?.floor,
      apartment: client.address?.apartment
    });
    
    // Parse address - handle both object and string formats
    let addressData = { street: '', number: '', floor: '', apartment: '', isHouse: false, countryCode: userCountryCode };
    
    if (client.address) {
      if (typeof client.address === 'string') {
        // If address is a string, parse it
        console.log('📝 Parsing address string:', client.address);
        const parsed = parseAddressString(client.address);
        console.log('📝 Parsed address:', parsed);
        addressData = {
          ...parsed,
          isHouse: false,
          countryCode: userCountryCode
        };
      } else if (typeof client.address === 'object') {
        // If address is an object, check if it needs parsing/reconstruction
        // Sometimes the object might have incorrect data (e.g., street split incorrectly)
        console.log('📝 Using address object directly:', client.address);
        
        // Check if address appears incorrectly split
        // Pattern: street="Bulevar", number="Vudroa", floor="Vilsona", apartment="78"
        // This suggests the address was split incorrectly
        const street = (client.address.street || '').trim();
        const number = (client.address.number || '').trim();
        const floor = (client.address.floor || '').trim();
        const apartment = (client.address.apartment || '').trim();
        
        const streetWords = street.split(/\s+/).filter(w => w.length > 0);
        const numberIsNotNumeric = number && !/^\d+[\d\/\-A-Za-z]*$/.test(number);
        const floorIsNotNumeric = floor && !/^\d+[\d\/\-A-Za-z]*$/.test(floor);
        const apartmentIsNumeric = apartment && /^\d+[\d\/\-A-Za-z]*$/.test(apartment);
        
        // Check if address is incorrectly split:
        // 1. If number or floor contains non-numeric text (letters), and apartment is numeric
        // 2. If street is short (1-2 words) and number/floor are non-numeric
        // 3. If number and floor are empty but street has multiple words (shouldn't parse)
        const isIncorrectlySplit = (
          (numberIsNotNumeric || floorIsNotNumeric) && 
          apartmentIsNumeric &&
          (streetWords.length <= 2 || (numberIsNotNumeric && floorIsNotNumeric))
        );
        
        // Also check: if number and floor are empty, but street has content, don't parse
        const hasValidNumber = number && /^\d+/.test(number);
        const hasValidFloor = floor && /^\d+/.test(floor);
        const hasValidApartment = apartment && /^\d+/.test(apartment);
        
        // If address already has valid numeric fields, use as-is (don't parse)
        if (hasValidNumber || hasValidFloor || hasValidApartment) {
          if (!isIncorrectlySplit) {
            // Use object as-is - address is correctly structured
            console.log('📝 Address object is correctly structured, using as-is');
            addressData = {
              street: street,
              number: number,
              floor: floor,
              apartment: apartment,
              isHouse: client.address.isHouse || false,
              countryCode: client.address.countryCode || userCountryCode
            };
          } else {
            // Likely incorrectly split - reconstruct from all fields
            console.log('📝 Address appears incorrectly split, reconstructing...');
            const reconstructedStreet = [
              street,
              numberIsNotNumeric ? number : '',
              floorIsNotNumeric ? floor : ''
            ].filter(Boolean).join(' ');
            addressData = {
              street: reconstructedStreet,
              number: apartmentIsNumeric ? apartment : number,
              floor: hasValidFloor ? floor : '',
              apartment: apartmentIsNumeric && !hasValidFloor ? '' : apartment,
              isHouse: client.address.isHouse || false,
              countryCode: client.address.countryCode || userCountryCode
            };
            console.log('📝 Reconstructed address:', addressData);
          }
        } else {
          // No valid numeric fields - use as-is (don't try to parse)
          console.log('📝 No valid numeric fields, using address as-is');
          addressData = {
            street: street,
            number: number,
            floor: floor,
            apartment: apartment,
            isHouse: client.address.isHouse || false,
            countryCode: client.address.countryCode || userCountryCode
          };
        }
      }
    }
    
    console.log('📝 Final addressData for form:', addressData);
    
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      countryCode: addressData.countryCode,
      street: addressData.street,
      number: addressData.number,
      floor: addressData.floor,
      apartment: addressData.apartment,
      isHouse: addressData.isHouse,
      description: client.description || '',
      rating: typeof client.rating === 'number' ? client.rating.toString() : ''
    });
    setShowForm(true);
  };
  
  const handleDelete = async (clientId) => {
    if (!clientId) {
      toast.error('ID klijenta nedostaje. Osvežite stranicu i pokušajte ponovo.');
      return;
    }
    
    if (window.confirm('Da li ste sigurni da želite da obrišete ovog klijenta?')) {
      // Store client before deletion to check if it was removed from state
      const clientBeforeDelete = clients.find(c => c._id === clientId);
      
      // Dispatch delete and check Redux state instead of using unwrap()
      const deleteResult = await dispatch(deleteClientAsync(clientId));
      
      // Check if the action was fulfilled (successful) by checking the result
      if (deleteClientAsync.fulfilled.match(deleteResult)) {
        // Delete was successful - Redux state is already updated
        toast.success('Klijent je uspešno obrisan');
        if (currentClient?._id === clientId) {
          resetForm();
        }
        if (expandedCard === clientId) {
          setExpandedCard(null);
        }
        await dispatch(fetchClientsAsync());
      } else if (deleteClientAsync.rejected.match(deleteResult)) {
        // Delete was rejected - check if it's a CompanyModel error
        const errorPayload = deleteResult.payload || {};
        const errorMessage = errorPayload.message || errorPayload.data?.error || '';
        const isCompanyModelError = errorMessage.includes("Cannot read properties of undefined (reading 'findById')") ||
                                   errorMessage.includes('findById');
        
        if (isCompanyModelError) {
          // Check Redux state - if client was removed, ignore the error
          const clientStillExists = clients.find(c => c._id === clientId);
          
          if (!clientStillExists || clientStillExists !== clientBeforeDelete) {
            // Client was removed from Redux state, ignore the error
            toast.success('Klijent je uspešno obrisan');
            if (currentClient?._id === clientId) {
              resetForm();
            }
            if (expandedCard === clientId) {
              setExpandedCard(null);
            }
            await dispatch(fetchClientsAsync());
            return; // Exit early, don't show error
          }
        }
        
        // For other errors or if client wasn't deleted, show error
        const errorMessageObj = errorPayload.message || errorPayload.data?.error || 'An error occurred while deleting the client';
        toast.error(errorMessageObj);
      }
    }
  };
  
  const showAddForm = () => {
    resetForm();
    setShowForm(true);
  };
  
  const businessType = useMemo(() => {
    return user?.result?.businessType || sessionStorage.getItem('businessType') || '';
  }, [user]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('sr-RS', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const normalizePhone = (value) => (value || '').replace(/\D/g, '');

  const formatClientAddress = (client) => {
    const a = client?.address;
    if (!a) return '';
    if (typeof a === 'string') return a;
    const line = [a.street, a.number].filter(Boolean).join(' ');
    const extra = [];
    if (!a.isHouse && a.floor) extra.push(`sprat ${a.floor}`);
    if (!a.isHouse && a.apartment) extra.push(`stan ${a.apartment}`);
    return [line, extra.join(', ')].filter(Boolean).join(', ');
  };

  const phoneHref = (phone) => {
    const digits = String(phone || '').replace(/[^\d+]/g, '');
    return digits ? `tel:${digits}` : null;
  };

  const buildPrefillFromClient = (client) => {
    if (!client) return null;
    const a = client.address;
    const clientAddress = a && typeof a === 'object'
      ? {
          street: a.street || '',
          number: a.number || '',
          floor: a.floor || '',
          apartment: a.apartment || '',
          isHouse: !!a.isHouse
        }
      : a;
    return {
      clientName: client.name || '',
      clientPhone: client.phone || '',
      clientEmail: client.email || '',
      clientAddress,
      clientId: client._id
    };
  };

  const clientHistoryJobs = useMemo(() => {
    if (!historyClient) return [];

    const normalizedPhone = normalizePhone(historyClient.phone);
    const normalizedName = historyClient.name?.toLowerCase?.() || '';
    const seen = new Set();

    return [...jobList, ...archiveJobList]
      .filter((job) => {
        if (!job || !job._id) return false;
        if (seen.has(String(job._id))) return false;

        let match = false;
        if (historyClient._id && job.clientId && job.clientId === historyClient._id) {
          match = true;
        } else {
          const jobPhone = normalizePhone(job.clientPhone);
          if (normalizedPhone && jobPhone && normalizedPhone === jobPhone) {
            match = true;
          } else if (normalizedName && job.clientName?.toLowerCase?.() === normalizedName) {
            match = true;
          }
        }
        if (!match) return false;
        seen.add(String(job._id));
        return true;
      })
      .sort((a, b) => {
        const aDate = new Date(a.serviceDate || a.createdAt || 0).getTime();
        const bDate = new Date(b.serviceDate || b.createdAt || 0).getTime();
        return bDate - aDate;
      });
  }, [historyClient, jobList, archiveJobList]);

  const archiveJobIdSet = useMemo(
    () => new Set(archiveJobList.map((job) => String(job._id))),
    [archiveJobList]
  );

  useEffect(() => {
    if (!historyClient) {
      lastHistoryJobsFetchKeyRef.current = null;
      return;
    }
    if (!businessType) return;
    const key = historyClient._id
      ? `id:${historyClient._id}`
      : `phone:${normalizePhone(historyClient.phone)}`;
    if (lastHistoryJobsFetchKeyRef.current === key) return;
    lastHistoryJobsFetchKeyRef.current = key;
    dispatch(getJobs(businessType));
    dispatch(fetchArchiveJobs({ businessType, fetchAll: true }));
  }, [historyClient, dispatch, businessType]);

  const handleHistory = (client) => {
    setHistoryClient(client);
  };

  const openCreateJobForClient = (client) => {
    setCreateJobPrefill(buildPrefillFromClient(client));
  };

  const handleCloseCreateJob = () => {
    setCreateJobPrefill(null);
    if (businessType) {
      dispatch(getJobs(businessType));
      dispatch(fetchArchiveJobs({ businessType, fetchAll: true }));
    }
  };

  const closeHistoryModal = () => {
    lastHistoryJobsFetchKeyRef.current = null;
    setHistoryClient(null);
    setCreateJobPrefill(null);
  };

  // Extract brands from jobs for each client
  const clientsWithBrands = useMemo(() => {
    return filteredClients.map(client => {
      // Find all jobs for this client
      const clientJobs = jobList.filter(job => {
        if (client._id && job.clientId === client._id) return true;
        const normalizedClientPhone = normalizePhone(client.phone);
        const normalizedJobPhone = normalizePhone(job.clientPhone);
        const normalizedClientName = client.name?.toLowerCase() || '';
        const normalizedJobName = job.clientName?.toLowerCase() || '';
        return normalizedClientPhone === normalizedJobPhone || 
               (normalizedClientName && normalizedClientName === normalizedJobName);
      });
      
      // Extract unique brands from jobs
      const brands = new Set();
      clientJobs.forEach(job => {
        if (job.deviceBrand && job.deviceBrand.trim()) {
          brands.add(job.deviceBrand.trim());
        }
      });
      
      return {
        ...client,
        brands: Array.from(brands)
      };
    });
  }, [filteredClients, jobList]);

  // Get all available brands from all clients with job counts
  const availableBrands = useMemo(() => {
    const brandMap = new Map();
    clientsWithBrands.forEach(client => {
      client.brands.forEach(brand => {
        if (brand && brand.trim()) {
          const brandKey = brand.trim();
          if (!brandMap.has(brandKey)) {
            brandMap.set(brandKey, 0);
          }
          // Count clients with this brand
          brandMap.set(brandKey, brandMap.get(brandKey) + 1);
        }
      });
    });
    // Convert to array and sort
    return Array.from(brandMap.entries())
      .map(([brand, count]) => ({ brand, count }))
      .sort((a, b) => a.brand.localeCompare(b.brand));
  }, [clientsWithBrands]);

  // Filter clients by brand and rating
  const filteredByBrandAndRating = useMemo(() => {
    return clientsWithBrands.filter(client => {
      // Brand filter
      if (selectedBrand !== 'all') {
        if (!client.brands.includes(selectedBrand)) {
          return false;
        }
      }
      
      // Rating filter
      if (selectedRating !== 'all') {
        const clientRating = typeof client.rating === 'number' ? client.rating : null;
        if (selectedRating === 'no-rating' && clientRating !== null) {
          return false;
        }
        if (selectedRating === 'has-rating' && clientRating === null) {
          return false;
        }
        if (selectedRating.startsWith('rating-')) {
          const minRating = parseInt(selectedRating.replace('rating-', ''));
          if (!clientRating || clientRating < minRating) {
            return false;
          }
        }
      }
      
      return true;
    });
  }, [clientsWithBrands, selectedBrand, selectedRating]);

  // Calculate pagination - sort by address (street + number)
  const sortedClients = useMemo(() => {
    return filteredByBrandAndRating
      .slice()
      .sort((a, b) => {
        // Build address string for comparison
        const addressA = `${a.address?.street || ''} ${a.address?.number || ''}`.trim().toLowerCase();
        const addressB = `${b.address?.street || ''} ${b.address?.number || ''}`.trim().toLowerCase();
        return addressA.localeCompare(addressB, 'sr', { sensitivity: 'base' });
      });
  }, [filteredByBrandAndRating]);

  const totalPages = Math.ceil(sortedClients.length / clientsPerPage);
  const startIndex = (currentPage - 1) * clientsPerPage;
  const endIndex = startIndex + clientsPerPage;
  const paginatedClients = sortedClients.slice(startIndex, endIndex);

  // Reset to first page if current page is out of bounds
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);
  
  const renderTableView = () => {
    return (
      <div className="overflow-x-auto rounded-lg shadow-lg">
        <table className="min-w-full bg-gray-800">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-bold text-blue-400 uppercase tracking-wider">Name</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-bold text-blue-400 uppercase tracking-wider">Contact</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-bold text-blue-400 uppercase tracking-wider">Address</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-bold text-blue-400 uppercase tracking-wider">Rating</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-bold text-blue-400 uppercase tracking-wider">Description</th>
              <th className="px-4 sm:px-6 py-3 text-right text-xs font-bold text-blue-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedClients.map((client) => (
                <tr key={client._id} className="hover:bg-gray-700 border-b border-gray-700 transition-colors">
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-white">{client.name}</div>
                </td>
                <td className="px-4 sm:px-6 py-4">
                  <div className="space-y-2">
                    <a 
                      href={`tel:${client.phone}`}
                      className="text-sm text-gray-300 hover:text-white flex items-center transition-colors"
                    >
                      <Phone size={14} className="mr-1 text-gray-400" />
                      {client.phone}
                    </a>
                    {client.email && (
                      <a 
                        href={`mailto:${client.email}`}
                        className="text-sm text-gray-300 hover:text-white flex items-center transition-colors"
                      >
                        <Mail size={14} className="mr-1 text-gray-400" />
                        {client.email}
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4">
                  <div className="text-sm text-gray-300">
                    {client.address?.street} {client.address?.number}
                    {!client.address?.isHouse && client.address?.floor && (
                      <span>, Floor {client.address.floor}</span>
                    )}
                    {!client.address?.isHouse && client.address?.apartment && (
                      <span>, Apt {client.address.apartment}</span>
                    )}
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4">
                  <div className="text-sm text-yellow-400">
                    {typeof client.rating === 'number' ? `${client.rating}/10` : '-'}
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4">
                  <div className="text-sm text-gray-400 max-w-xs truncate">
                    {client.description || '-'}
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4 text-right">
                  <div className="flex justify-end items-center gap-2">
                    <button
                      onClick={() => handleHistory(client)}
                      className="text-amber-400 hover:text-amber-300 transition-colors"
                      title="Podaci i istorija"
                    >
                      <List size={16} />
                    </button>
                    <button
                      onClick={() => handleEdit(client)}
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                      title="Edit Client"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(client._id)}
                      className="text-red-400 hover:text-red-300 transition-colors"
                      title="Delete Client"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
              ))}
          </tbody>
        </table>
      </div>
    );
  };

  const [expandedCard, setExpandedCard] = useState(null);

  const renderCardView = () => {
    return (
      <div className="grid grid-cols-1 gap-4">
        {paginatedClients.map(client => (
          <div
            key={client._id}
            className="bg-gray-800 p-4 rounded-lg shadow"
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-medium text-white">{client.name}</h3>
                <div className="text-sm text-gray-300 mt-1">
                  {client.address?.street} {client.address?.number}
                  {!client.address?.isHouse && client.address?.floor && (
                    <span>, Floor {client.address.floor}</span>
                  )}
                  {!client.address?.isHouse && client.address?.apartment && (
                    <span>, Apt {client.address.apartment}</span>
                  )}
                </div>
                {typeof client.rating === 'number' && (
                  <div className="text-sm text-yellow-400 font-semibold mt-1">
                    Ocena: {client.rating}/10
                  </div>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleHistory(client)}
                  className="text-amber-400 hover:text-amber-300"
                  title="Podaci i istorija"
                >
                  <List size={16} />
                </button>
                <button
                  onClick={() => handleEdit(client)}
                  className="text-blue-400 hover:text-blue-300"
                  title="Edit Client"
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={() => handleDelete(client._id)}
                  className="text-red-400 hover:text-red-300"
                  title="Delete Client"
                >
                  <Trash2 size={16} />
                </button>
                <button
                  onClick={() => setExpandedCard(expandedCard === client._id ? null : client._id)}
                  className="text-blue-400 hover:text-blue-300"
                >
                  {expandedCard === client._id ? <X size={16} /> : <FileText size={16} />}
                </button>
              </div>
            </div>
            
            {(expandedCard === client._id) && (
              <div className="mt-4 space-y-3 pt-4 border-t border-gray-700">
                <a 
                  href={`tel:${client.phone}`}
                  className="flex items-center text-gray-300 hover:text-gray-200"
                >
                  <Phone size={16} className="mr-2 text-gray-400" />
                  {client.phone}
                </a>
                
                {client.email && (
                  <a 
                    href={`mailto:${client.email}`}
                    className="flex items-center text-gray-300 hover:text-gray-200"
                  >
                    <Mail size={16} className="mr-2 text-gray-400" />
                    {client.email}
                  </a>
                )}
                
                {client.description && (
                  <div className="flex items-start text-gray-400">
                    <FileText size={16} className="mr-2 mt-1 flex-shrink-0" />
                    <span>{client.description}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderPhoneInput = () => {
    return (
      <div className="form-group">
        <CustomPhoneInput
          value={formData.phone || defaultPhonePrefix}
          onChange={(value) => {
            setFormData(prev => ({
              ...prev,
              phone: value || defaultPhonePrefix
            }));
          }}
          defaultCountryCode={userCountryCode}
          placeholder="Enter phone number"
          required={false}
        />
      </div>
    );
  };

  const renderForm = () => (
    <div className="bg-gray-800 rounded-lg shadow-lg">
      <div className="border-b border-gray-700 p-4 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">
          {currentClient ? 'Edit Client' : 'Add New Client'}
        </h2>
        <button 
          onClick={resetForm}
          className="text-gray-400 hover:text-white"
        >
          <X size={20} />
        </button>
      </div>
      
      {formError && (
        <div className="mx-4 mt-4 p-3 bg-red-900/50 border border-red-500 text-red-200 rounded text-sm">
          {formError}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="p-4">
        <div className="space-y-4">
          <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="Client Name"
          className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          autoCapitalize="words"
          autoCorrect="off"
          spellCheck={false}
          />

          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Email"
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />

          {renderPhoneInput()}

          <input
            type="text"
            name="street"
            value={formData.street}
            onChange={handleInputChange}
          placeholder="Street Name"
          className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          autoCapitalize="words"
          autoCorrect="off"
          spellCheck={false}
          />

          <div className="flex items-center space-x-2">
            <input
              type="text"
              name="number"
              value={formData.number}
              onChange={handleInputChange}
              placeholder="No."
              className="w-20 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            
            {!formData.isHouse && (
              <>
                <input
                  type="text"
                  name="floor"
                  value={formData.floor}
                  onChange={handleInputChange}
                  placeholder="Floor"
                  className="w-20 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <input
                  type="text"
                  name="apartment"
                  value={formData.apartment}
                  onChange={handleInputChange}
                  placeholder="Apt"
                  className="w-20 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </>
            )}
            
            <label className="flex items-center space-x-2 text-sm text-white">
              <input
                type="checkbox"
                name="isHouse"
                checked={formData.isHouse}
                onChange={(e) => handleInputChange({
                  target: {
                    name: 'isHouse',
                    value: e.target.checked
                  }
                })}
                className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
              />
              <span>House</span>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0">
            <label className="text-sm text-white sm:w-32">Ocena klijenta</label>
            <select
              name="rating"
              value={formData.rating}
              onChange={handleInputChange}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Bez ocene</option>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>

          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Description"
            rows="3"
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          ></textarea>
        </div>

        <div className="flex justify-end space-x-2 mt-4 border-t border-gray-700 pt-4">
          <button 
            type="button" 
            onClick={resetForm}
            className="px-3 py-2 bg-gray-700 text-white text-sm rounded hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
          >
            {currentClient ? 'Update Client' : 'Add Client'}
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <>
      {historyClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-gray-900 w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-xl border border-gray-700 shadow-2xl flex flex-col">
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-800">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-0.5">Klijent</p>
                <h3 className="text-lg font-semibold text-white truncate">{historyClient.name || 'Nepoznat klijent'}</h3>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => openCreateJobForClient(historyClient)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-400 text-white text-sm font-medium rounded-lg transition-colors"
                  title="Novi posao za ovog klijenta"
                >
                  <PlusCircle size={16} />
                  <span className="hidden sm:inline">Novi posao</span>
                </button>
                <button
                  onClick={closeHistoryModal}
                  className="text-gray-400 hover:text-white transition-colors p-1"
                  aria-label="Zatvori"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="p-5 overflow-y-auto space-y-5">
              {(() => {
                const callHref = phoneHref(historyClient.phone);
                const addressText = formatClientAddress(historyClient);
                return (
                  <div className="rounded-xl border border-gray-700 bg-gray-800/60 p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
                        {callHref ? (
                          <a
                            href={callHref}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-colors"
                          >
                            <Phone size={16} />
                            Pozovi
                          </a>
                        ) : null}
                        {historyClient.phone ? (
                          <a href={callHref || undefined} className="text-gray-200 hover:text-white">
                            {historyClient.phone}
                          </a>
                        ) : (
                          <span className="text-gray-500">Nema telefona</span>
                        )}
                      </div>
                      {historyClient.email ? (
                        <div className="flex items-start gap-2 text-gray-300">
                          <Mail size={15} className="mt-0.5 text-gray-500 flex-shrink-0" />
                          <a href={`mailto:${historyClient.email}`} className="hover:text-white break-all">
                            {historyClient.email}
                          </a>
                        </div>
                      ) : null}
                      {addressText ? (
                        <div className="flex items-start gap-2 text-gray-300">
                          <MapPin size={15} className="mt-0.5 text-gray-500 flex-shrink-0" />
                          <span>{addressText}</span>
                        </div>
                      ) : null}
                      <div className="flex items-center gap-2 text-yellow-400">
                        <Star size={15} className="flex-shrink-0" />
                        <span>{typeof historyClient.rating === 'number' ? `${historyClient.rating}/10` : 'Nema ocene'}</span>
                      </div>
                      {historyClient.description ? (
                        <div className="sm:col-span-2 flex items-start gap-2 text-gray-400">
                          <FileText size={15} className="mt-0.5 text-gray-500 flex-shrink-0" />
                          <span>{historyClient.description}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })()}

              <div>
                <h4 className="text-sm font-semibold text-white mb-3">Istorija popravki</h4>
                {(jobsLoading || archiveLoading) && clientHistoryJobs.length === 0 ? (
                  <div className="flex justify-center py-10">
                    <LoadingSpinner size="lg" color="white" />
                  </div>
                ) : clientHistoryJobs.length === 0 ? (
                  <div className="text-center text-gray-400 py-8 rounded-xl border border-dashed border-gray-700">
                    <p>Za ovog klijenta trenutno nema evidentiranih popravki.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {clientHistoryJobs.map((job) => {
                      const note =
                        job.report?.workDescription ||
                        job.report?.additionalNotes ||
                        job.issueDescription;
                      const revenueStr = job.report?.serviceCharged || '0';
                      const revenue =
                        parseFloat(
                          revenueStr
                            .toString()
                            .replace(/[^\d.,]/g, '')
                            .replace(',', '.')
                        ) || 0;
                      const totalCost = job.report?.totalCost || 0;
                      const profit =
                        job.report?.profit !== undefined
                          ? job.report.profit
                          : revenue - totalCost;
                      const deviceLine = [job.deviceBrand, job.deviceModel, job.deviceType || job.deviceCategoryName]
                        .filter(Boolean)
                        .join(' ');
                      const statusLabel = getJobDisplayStatus(job) || job.status || t('common.notAvailable');
                      const statusLower = String(job.status || '').toLowerCase();
                      const statusClass =
                        statusLower.includes('archiv') || statusLower.includes('finish') || statusLower.includes('complet')
                          ? 'bg-emerald-900/50 text-emerald-200'
                          : statusLower.includes('cancel')
                            ? 'bg-rose-900/50 text-rose-200'
                            : 'bg-sky-900/50 text-sky-200';
                      const isArchived = archiveJobIdSet.has(String(job._id));
                      return (
                        <div
                          key={job._id}
                          className="rounded-xl border border-gray-700 bg-gray-800/40 p-4 hover:border-gray-600 transition-colors"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2 text-sm text-gray-200">
                              <Calendar size={14} className="text-gray-500" />
                              {formatDateTime(job.serviceDate || job.createdAt)}
                            </div>
                            <div className="flex items-center gap-1.5">
                              {isArchived ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-700 text-gray-300">
                                  Arhiva
                                </span>
                              ) : null}
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
                                {statusLabel}
                              </span>
                            </div>
                          </div>
                          {deviceLine ? (
                            <p className="text-sm text-gray-300 mb-1">{deviceLine}</p>
                          ) : null}
                          {note ? (
                            <p className="text-sm text-gray-400 mb-3" title={note}>
                              {formatDisplayValue(note)}
                            </p>
                          ) : null}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-400">
                            {job.assignedTo ? (
                              <div className="flex items-center gap-2">
                                <div
                                  className="h-5 w-5 rounded-full flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0 shadow-sm"
                                  style={{ backgroundColor: getWorkerColor(job.assignedTo, workers), background: getWorkerColor(job.assignedTo, workers) }}
                                  title={getWorkerName(job.assignedTo, workers, partners)}
                                >
                                  {getWorkerInitials(job.assignedTo, workers, partners)}
                                </div>
                                <span className="text-gray-300">{getWorkerName(job.assignedTo, workers, partners)}</span>
                              </div>
                            ) : null}
                            {job.report?.customerRating !== undefined && job.report?.customerRating !== null ? (
                              <span className="inline-flex items-center gap-1 text-yellow-300">
                                <Star size={12} />
                                {job.report.customerRating}/10
                              </span>
                            ) : null}
                            <span>
                              Naplata:{' '}
                              <span className="text-green-300">{job.report?.serviceCharged || '-'}</span>
                            </span>
                            <span>
                              Materijal:{' '}
                              <span className="text-gray-300">{job.report?.materialsUsed || '-'}</span>
                            </span>
                            <span>
                              Zarada:{' '}
                              {profit ? (
                                <span className={profit >= 0 ? 'text-green-300' : 'text-red-300'}>
                                  {profit.toLocaleString('sr-RS')} RSD
                                </span>
                              ) : (
                                <span className="text-gray-500">-</span>
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {createJobPrefill && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h2 className="text-lg font-semibold text-white">Novi posao za klijenta</h2>
              <button
                onClick={() => setCreateJobPrefill(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
              <JobForm
                isEdit={false}
                jobData={createJobPrefill}
                onClose={handleCloseCreateJob}
                isModal={true}
              />
            </div>
          </div>
        </div>
      )}

      <div className="bg-gray-900 min-h-screen p-2 sm:p-4">
        {/* Header */}
        <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 shadow-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
            <div className="flex-1">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-0">Clients</h2>
              {user && (() => {
                const currentPackage = getCurrentPackage(user);
                const maxClients = getMaxClients(user);
                const canAdd = canAddClient(user, sortedClients.length);
                if (maxClients !== Infinity) {
                  return (
                    <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg px-3 py-2 text-xs sm:text-sm mt-2 sm:mt-0">
                      <div className="text-yellow-400 font-medium">
                        {t('superAdmin.clientsUsed', 'Iskorišćeno klijenata')}: {sortedClients.length} / {maxClients}
                      </div>
                      {!canAdd && (
                        <div className="text-red-400 text-xs mt-1">
                          {t('superAdmin.addressLimitReached', 'Limit dostignut!')}
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            {!showForm && (() => {
              const canAdd = user ? canAddClient(user, sortedClients.length) : true;
              return (
                <button 
                  onClick={() => {
                    if (!canAdd) {
                      toast.error(`Dostigli ste limit klijenata za vaš paket. Maksimalno dozvoljeno: ${getMaxClients(user)}. Nadogradite paket za više klijenata.`);
                      return;
                    }
                    showAddForm();
                  }}
                  disabled={!canAdd}
                  className={`flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 text-sm rounded-lg transition-colors ${
                    canAdd
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                  }`}
                  title={!canAdd ? `Dostigli ste limit klijenata za vaš paket. Maksimalno dozvoljeno: ${getMaxClients(user)}.` : ''}
                >
                  <UserPlus size={18} />
                  <span className="hidden sm:inline">Add New Client</span>
                  <span className="sm:hidden">Add</span>
                </button>
              );
            })()}
          </div>
        </div>

        {!showForm && (
          <div className="mb-6 space-y-3">
            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              {/* Brand Filter */}
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs text-gray-400 mb-1">Filter po brendu</label>
                <select
                  value={selectedBrand}
                  onChange={(e) => {
                    setSelectedBrand(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  title={availableBrands.length > 0 ? `Dostupni brendovi: ${availableBrands.map(b => b.brand).join(', ')}` : 'Nema dostupnih brendova'}
                >
                  <option value="all">Svi brendovi ({availableBrands.length})</option>
                  {availableBrands.length > 0 ? (
                    availableBrands.map(({ brand, count }) => (
                      <option key={brand} value={brand}>
                        {brand} ({count} {count === 1 ? 'klijent' : 'klijenata'})
                      </option>
                    ))
                  ) : (
                    <option value="all" disabled>Nema dostupnih brendova</option>
                  )}
                </select>
                {availableBrands.length > 0 && (
                  <div className="text-[10px] text-gray-500 mt-1">
                    {availableBrands.length} {availableBrands.length === 1 ? 'brend' : 'brendova'} dostupno
                  </div>
                )}
              </div>
              
              {/* Rating Filter */}
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs text-gray-400 mb-1">Filter po oceni</label>
                <select
                  value={selectedRating}
                  onChange={(e) => {
                    setSelectedRating(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="all">Sve ocene</option>
                  <option value="no-rating">Bez ocene</option>
                  <option value="has-rating">Sa ocenom</option>
                  <option value="rating-8">8+</option>
                  <option value="rating-9">9+</option>
                  <option value="rating-10">10</option>
                </select>
              </div>
              
              {/* Clear Filters Button */}
              {(selectedBrand !== 'all' || selectedRating !== 'all') && (
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSelectedBrand('all');
                      setSelectedRating('all');
                      setCurrentPage(1);
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500 transition-colors text-sm flex items-center gap-2"
                  >
                    <X size={14} />
                    Obriši filtere
                  </button>
                </div>
              )}
            </div>
            
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                name="searchTerm"
                placeholder="Pretraga po imenu, telefonu, emailu ili adresi..."
                value={searchTerm}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-2 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    dispatch(searchClients(''));
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <p className="text-sm text-gray-400 mt-1 ml-1">
              {sortedClients.length === 0 && (searchTerm || selectedBrand !== 'all' || selectedRating !== 'all')
                ? 'Nema rezultata' 
                : sortedClients.length < clients.length 
                  ? `Pronađeno ${sortedClients.length} klijenata` 
                  : ''}
            </p>
          </div>
        )}

        {showForm ? (
          renderForm()
        ) : loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : filteredClients.length > 0 ? (
          <div>
            <div className="hidden md:block">
              {renderTableView()}
            </div>
            <div className="md:hidden">
              {renderCardView()}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-400">
                  Showing {startIndex + 1} to {Math.min(endIndex, sortedClients.length)} of {sortedClients.length} clients
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  >
                    <ChevronLeft size={16} />
                    <span className="hidden sm:inline">Previous</span>
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-2 rounded-md transition-colors ${
                              currentPage === page
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        page === currentPage - 2 ||
                        page === currentPage + 2
                      ) {
                        return (
                          <span key={page} className="px-2 text-gray-400">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  >
                    <span className="hidden sm:inline">Next</span>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12">
            <UserX size={48} className="mx-auto text-gray-500 mb-4" />
            <h3 className="text-xl font-medium text-gray-300 mb-2">No clients yet</h3>
            <p className="text-gray-400 mb-4">
              You haven't added any clients to your list
            </p>
            <button 
              onClick={showAddForm}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <PlusCircle size={16} className="mr-2" />
              Add your first client
            </button>
          </div>
        ) : (
          <div className="text-center py-12">
            <Search size={48} className="mx-auto text-gray-500 mb-4" />
            <h3 className="text-xl font-medium text-gray-300 mb-2">No matches found</h3>
            <p className="text-gray-400 mb-4">
              No clients match your search criteria: "{searchTerm}"
            </p>
            <button 
              onClick={() => {
                setSearchTerm('');
                dispatch(searchClients(''));
              }}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <X size={16} className="mr-2" />
              Clear search
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default ClientsView; 